import { useState, useMemo, useEffect } from "react";
import { MultiplicityType } from "@sa/rep-core";
import { DataType } from "@sa/rep-common";

import { SlidePayload } from "../../../../subscriptions/types";

import {
  ISubscriptionProviderContextValue,
  ISubscriptionProviderContextActions,
  ISubscriptionProviderContextState,
  ISubscriptionProviderProps,
  SlidePayloadParams,
  ApplyParameters,
} from "./SubscriptionProvider.types";

export function useSubscriptionState(props: ISubscriptionProviderProps): ISubscriptionProviderContextValue {
  const { reportId, payload } = props;

  const [initialPayload] = useState<SlidePayload | undefined>(payload);
  const [applyParametersFunction, setApplyParametersFunction] = useState<(parameters: ApplyParameters) => void | undefined>();

  const [title, setTitle] = useState<string | null>(null);
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [parameters, setParameters] = useState<SlidePayloadParams | undefined>(undefined);

  const currentPayload = useMemo<SlidePayload | undefined>(() => {
    const isNotRequiredFieldsFilled = parameters === undefined || title === null || source === null;
    const isNotAllParametersFilled = Object.values(parameters || {}).some((v) => {
      const isNullOrUndefined = v === null || v === undefined;
      const isEmptyArray = Array.isArray(v) && v.length === 0;
      return isNullOrUndefined || isEmptyArray;
    });

    if (isNotRequiredFieldsFilled || isNotAllParametersFilled) {
      return undefined;
    }

    return {
      title,
      subtitle: subtitle || "",
      source,
      reportDefinition: {
        id: reportId,
        names: { 9: title },
        parameters: Object.keys(parameters).map((key) => {
          const value = parameters[key];
          return {
            id: key,
            type: DataType.Text,
            multiplicityType: Array.isArray(value) ? MultiplicityType.Multiple : MultiplicityType.Single,
            value: (Array.isArray(value) ? value : [value]).map(String),
          };
        }),
      },
    };
  }, [title, subtitle, source, parameters]);

  const applyPayload = useMemo(() => {
    if (!applyParametersFunction) {
      return undefined;
    }
    return (payload: SlidePayload) => {
      const parameters = payload.reportDefinition.parameters.reduce<ApplyParameters>((acc, p) => ({ ...acc, [p.id]: p }), {});
      applyParametersFunction(parameters);
    };
  }, [applyParametersFunction]);

  useEffect(() => {
    if (initialPayload && applyPayload) {
      applyPayload(initialPayload);
    }
  }, [applyPayload, initialPayload]);

  const state = useMemo<ISubscriptionProviderContextState>(
    () => ({
      currentPayload,
      initialPayload,
      isSubscriptionLoading: false,
      isSubscriptionDisabled: !currentPayload,
    }),
    [initialPayload, currentPayload]
  );

  const actions = useMemo<ISubscriptionProviderContextActions>(
    () => ({
      setParameters,
      setSource,
      setSubtitle,
      setTitle,
      setApplyParametersFunction: (func) => setApplyParametersFunction(() => func),
      applyPayload,
    }),
    [setParameters, setSource, setSubtitle, setTitle, setApplyParametersFunction, applyParametersFunction, applyPayload]
  );

  const value = useMemo(() => ({ state, actions }), [actions, state]);

  return value;
}
